/**
 * DataRequestCard — one of Merlin's data requests ("asks"): a request filed
 * to sales-site staff for information, rendered in place inside the case or
 * conversation it belongs to (AsksSection). Expand for the thread; open
 * requests can be answered in-portal, nudged ("Request again") or dismissed.
 * An answered-but-unviewed request carries a red NEW badge until the reader
 * presses Mark seen (proposal 1: expanding or revealing a card never marks it
 * seen; leaving a page cleared every answer at once).
 *
 * ANSWER-FIRST (Scott/Jess check-in 2026-09-08): when a request has an answer
 * the answer must be the first thing the reader sees, not the last block of a
 * long card. So: the collapsed head carries a one-line excerpt of the newest
 * answer; the expanded body pins the newest answer at the top (highlighted)
 * and folds the original question under a "Show question" toggle, with the
 * rest of the thread and the reply box below.
 *
 * `reveal` is the host's "put this one in front of the reader" — the card
 * expands itself and scrolls into view, once per request.
 */
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  answerDataRequest,
  dismissDataRequest,
  markDataRequestSeen,
  messageDataRequest,
  nudgeDataRequest,
  type DataRequestResponse,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { trackEvent } from '../../services/portalTracking';
import { DismissControl } from '../quality/DismissControl';
import { formatTimeAgo } from '../../utils/dateFormatters';
import {
  answerExcerpt,
  splitThread,
  threadEntryKey,
  type ThreadEntry,
} from './dataRequestThread';
import { handoffWords, isHandedPassAlong } from '../quality/askKind';
import styles from './DataRequestCard.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

const STATUS_LABEL: Record<DoorDataRequestRead['status'], string> = {
  // A draft normally renders in DraftAskCard, not here; the label exists so
  // the map stays total and a draft reaching this card still reads honestly.
  proposed: 'Not sent yet',
  open: 'Open',
  answered: 'Answered',
  closed: 'Closed',
  cancelled: 'Dismissed',
};

const AUDIENCE_LABEL: Record<DoorDataRequestRead['audience'], string> = {
  user: '',
  team: 'Team',
  company: 'Everyone',
};

function ThreadEntryView({ entry }: { entry: ThreadEntry }) {
  if (entry.kind === 'answer') {
    return (
      <div className={styles.response}>
        <div className={styles.responseHead}>
          <span className={styles.responder}>
            {entry.response.responder_identifier}
          </span>
          <span className={styles.responseVia}>
            via {entry.response.channel} · {formatTimeAgo(entry.response.created_at)}
          </span>
        </div>
        <p className={styles.responseBody}>{entry.response.body_text}</p>
        <ResponseAttachments response={entry.response} />
      </div>
    );
  }
  return (
    <div className={styles.threadMessage}>
      <div className={styles.responseHead}>
        <span className={styles.responder}>{entry.message.author_label}</span>
        <span className={styles.responseVia}>
          {formatTimeAgo(entry.message.created_at)}
        </span>
      </div>
      <p className={styles.responseBody}>{entry.message.body_text}</p>
    </div>
  );
}

function ResponseAttachments({ response }: { response: DataRequestResponse }) {
  if (response.attachments.length === 0) return null;
  return (
    <div className={styles.attachments}>
      {response.attachments.map((a) => (
        <span key={a.id} className={styles.attachment} title={a.filename}>
          📎 {a.filename}
        </span>
      ))}
    </div>
  );
}

export function DataRequestCard({
  request,
  reveal = false,
  onRequestUpdated,
}: {
  request: DoorDataRequestRead;
  /**
   * True for the request the host wants in front of the reader right now
   * (its first unseen answer on open): the card expands itself and scrolls
   * into view. It does not mark the answer seen.
   */
  reveal?: boolean;
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
}) {
  // A read-only viewer reads the ask and its answers; it marks, answers and dismisses nothing (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [questionShown, setQuestionShown] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [saving, setSaving] = useState(false);
  const [nudged, setNudged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const { latestAnswer, rest: olderThread } = splitThread(request);
  const audienceLabel = AUDIENCE_LABEL[request.audience] ?? '';
  // A reply thread stays open while the request can still receive one.
  const canReply = request.status === 'open' || request.status === 'answered';

  const statusClass = {
    proposed: styles.chipOpen,
    open: styles.chipOpen,
    answered: styles.chipAnswered,
    closed: styles.chipClosed,
    cancelled: styles.chipCancelled,
  }[request.status];

  // Only the reader's own click clears the NEW badge (proposal 1).
  const markSeen = async () => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    trackEvent('action', 'answers_mark_seen', {
      data_request_id: request.id,
      surface: 'ask_card',
      result: 'request_sent',
    });
    try {
      onRequestUpdated(await markDataRequestSeen(request.id));
    } catch {
      setActionError('Could not mark it seen — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const toggleExpanded = () => setExpanded((v) => !v);

  // Host asked for this request (its answer unseen): put it in front of the
  // reader — expand (state adjusted during render, the React-sanctioned
  // "react to a prop change" pattern), then scroll into view.
  const [revealApplied, setRevealApplied] = useState(false);
  if (reveal && !revealApplied) {
    setRevealApplied(true);
    setExpanded(true);
  } else if (!reveal && revealApplied) {
    setRevealApplied(false);
  }
  const revealedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!reveal || revealedFor.current === request.id) return;
    revealedFor.current = request.id;
    // jsdom has no scrollIntoView; browsers do.
    rootRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [reveal, request.id]);

  const saveAnswer = async () => {
    const text = answerText.trim();
    if (!text || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const updated = await answerDataRequest(request.id, text);
      onRequestUpdated(updated);
      setAnswering(false);
      setAnswerText('');
    } catch {
      setActionError('Could not save the answer — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || replying) return;
    setReplying(true);
    setActionError(null);
    try {
      const updated = await messageDataRequest(request.id, text);
      onRequestUpdated(updated);
      setReplyText('');
    } catch {
      setActionError('Could not send the message — please try again.');
    } finally {
      setReplying(false);
    }
  };

  const nudge = async () => {
    if (nudged || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await nudgeDataRequest(request.id);
      onRequestUpdated(updated);
      setNudged(true);
    } catch {
      setActionError('Could not queue the follow-up — please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Dismiss: one click, or with a note Merlin learns from (kanban 59a1ee04).
  // The control records the press and shows a refusal.
  const dismiss = async (note: string | null) => {
    if (busy) throw new Error('busy');
    setBusy(true);
    setActionError(null);
    try {
      onRequestUpdated(await dismissDataRequest(request.id, undefined, note));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`${styles.card} ${reveal ? styles.cardRevealed : ''}`}
      data-status={request.status}
    >
      <button
        type="button"
        className={styles.cardHead}
        onClick={toggleExpanded}
        aria-expanded={expanded}
      >
        <span className={styles.cardHeadTop}>
          <span className={`${styles.chip} ${statusClass}`}>
            {request.peer_answered_by
              ? `Told — ${request.peer_answered_by.split(' ')[0]} answered`
              : isHandedPassAlong(request)
                ? `Passed along · ${handoffWords(request.handoff, request.target_name.split(' ')[0] || request.target_name).split(' · ')[0]}`
                : STATUS_LABEL[request.status]}
          </span>
          {audienceLabel && (
            <span className={`${styles.chip} ${styles.chipAudience}`}>
              {audienceLabel}
            </span>
          )}
          {request.answer_unseen && (
            <span className={styles.newBadge} aria-label="New answer">
              NEW
            </span>
          )}
          {request.recipient_done && request.status === 'open' && (
            // "Not mine" in the portal (kanban 2a28d4c0): still open, the
            // person's note is in the responses; re-aim it at someone else.
            <span className={styles.doneBadge} aria-label="Recipient says it is not theirs">
              said not theirs
            </span>
          )}
          {request.recipient_done && request.status !== 'open' && (
            <span className={styles.doneBadge} aria-label="Marked done by recipient">
              ✓ done
            </span>
          )}
          <span className={styles.cardTime}>
            {formatTimeAgo(request.created_at)}
          </span>
        </span>
        <span className={styles.cardTitle}>{request.question_title}</span>
        <span className={styles.cardMeta}>
          → {request.target_name}
          {request.followup_count > 0 &&
            ` · ${request.followup_count} follow-up${request.followup_count > 1 ? 's' : ''}`}
        </span>
        {request.context_summary && (
          <span className={styles.cardContext}>{request.context_summary}</span>
        )}
        {request.status === 'cancelled' && request.dismissed_note && (
          <span className={styles.cardContext} data-testid="dismissed-note">
            Dismissed: &ldquo;{request.dismissed_note}&rdquo;
          </span>
        )}
        {latestAnswer && !expanded && (
          <span className={styles.cardAnswer} data-testid="answer-excerpt">
            <span className={styles.cardAnswerLabel}>
              ↩ {latestAnswer.responder_identifier}
            </span>{' '}
            {answerExcerpt(latestAnswer)}
          </span>
        )}
      </button>

      {expanded && (
        <div className={styles.cardBody}>
          {latestAnswer && (
            <div className={styles.latestAnswer} data-testid="latest-answer">
              <div className={styles.latestAnswerLabel}>
                {request.responses.length > 1 ? 'Latest answer' : 'Answer'}
              </div>
              <div className={styles.responseHead}>
                <span className={styles.responder}>
                  {latestAnswer.responder_identifier}
                </span>
                <span className={styles.responseVia}>
                  via {latestAnswer.channel} · {formatTimeAgo(latestAnswer.created_at)}
                </span>
              </div>
              <p className={styles.responseBody}>{latestAnswer.body_text}</p>
              <ResponseAttachments response={latestAnswer} />
              {request.answer_unseen && !readOnly && (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => void markSeen()}
                    disabled={busy}
                  >
                    Mark seen
                  </button>
                </div>
              )}
            </div>
          )}

          {latestAnswer ? (
            <div className={styles.questionFold}>
              <button
                type="button"
                className={styles.questionToggle}
                onClick={() => setQuestionShown((v) => !v)}
                aria-expanded={questionShown}
              >
                {questionShown ? '▾ Hide question' : '▸ Show question'}
              </button>
              {questionShown && (
                <div className={styles.question}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {request.question_body}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.question}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {request.question_body}
              </ReactMarkdown>
            </div>
          )}

          {olderThread.length > 0 && (
            <div className={styles.responses}>
              {latestAnswer && (
                <div className={styles.threadLabel}>Earlier in this thread</div>
              )}
              {olderThread.map((entry) => (
                <ThreadEntryView key={threadEntryKey(entry)} entry={entry} />
              ))}
            </div>
          )}

          {canReply && !readOnly && (
            <div className={styles.replyForm}>
              <textarea
                className={styles.answerInput}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${request.target_name} in this thread…`}
                rows={2}
                aria-label="Reply in thread"
                disabled={replying}
              />
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionPrimary}`}
                onClick={() => void sendReply()}
                disabled={replying || !replyText.trim()}
              >
                {replying ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          )}

          {request.status === 'open' && !readOnly && (
            <div className={styles.actions}>
              {!answering ? (
                <>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => setAnswering(true)}
                    disabled={busy}
                  >
                    ✍ Answer myself
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => void nudge()}
                    disabled={busy || nudged}
                  >
                    ↻ Request again
                  </button>
                  {nudged && (
                    <span className={styles.nudgedNote}>Follow-up queued</span>
                  )}
                  <DismissControl
                    what={`the ask to ${request.target_name}`}
                    label="✕ Dismiss"
                    onDismiss={dismiss}
                    disabled={busy}
                    danger
                    track={{
                      plain: 'qcp_ask_dismiss',
                      note: 'qcp_ask_dismiss_note',
                      form: 'qcp_ask_reason',
                      targets: { data_request_id: request.id },
                      context: { data_request_id: request.id, surface: 'ask_card', state: request.status },
                    }}
                  />
                </>
              ) : (
                <div className={styles.answerForm}>
                  <textarea
                    className={styles.answerInput}
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Type the answer Merlin is waiting for…"
                    rows={3}
                    aria-label="Your answer"
                    disabled={saving}
                  />
                  <div className={styles.answerBtns}>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.actionPrimary}`}
                      onClick={() => void saveAnswer()}
                      disabled={saving || !answerText.trim()}
                    >
                      {saving ? 'Saving…' : 'Save answer'}
                    </button>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={() => {
                        setAnswering(false);
                        setActionError(null);
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {actionError && (
            <div className={styles.actionError} role="alert">
              {actionError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DataRequestCard;
